/* eslint-disable @typescript-eslint/naming-convention */
import { AaeClientQuestionStatus, AaeQuestionStatus, AaeResponseType } from '../enum/aae.enum';
import { Country, ModuleType, SubscriptionFeeType } from '../enum/ui-utils.enum';
import { ProgramType, WebinarType } from '../enum/webinar-course.enum';
import { EnumOptionInterface, EnumOptionProperties } from '../models/ui.model';

export enum Language {
    EN = 1,
    CH = 2,
}

export const LANGUAGE_LOCAL_TRANSLATION = {
    'en-us': 'language.english',
    'zh-cn': 'language.mandarin',
};

export function getLanguageTranslation(key: string): string {
    return LANGUAGE_LOCAL_TRANSLATION[key as keyof typeof LANGUAGE_LOCAL_TRANSLATION];
}

export const LANGUAGE_SELECTIONS: EnumOptionInterface = {
    [Language['EN']]: { label: 'text.englishShort', cssClass: '' },
    [Language['CH']]: { label: 'text.mandarinShort', cssClass: '' },
};

export const LANGUAGE_LONG_NAME: EnumOptionInterface = {
    [Language['EN']]: { label: 'text.english', cssClass: '' },
    [Language['CH']]: { label: 'text.mandarin', cssClass: '' },
};

export function getLanguageOptions(option: Language): EnumOptionProperties {
    return LANGUAGE_SELECTIONS[option] || new EnumOptionProperties();
}

export function getLongLanguageOptions(option: Language): EnumOptionProperties {
    return LANGUAGE_LONG_NAME[option] || new EnumOptionProperties();
}

export const PROGRAM_IMAGE_PLACEHOLDER = '../../../assets/images/program_cover_image_placeholder.png';

export const SUBSCRIPTION_STATUS: { [key: string]: EnumOptionProperties } = {
    true: { label: 'text.active', cssClass: 'text-[#30533A] bg-[#D1F6D8]' },
    false: { label: 'text.inactive', cssClass: 'text-[#5A1C1C] bg-[#F8D6D6]' },
};

export function getSubscriptionStatusOption(option: boolean): EnumOptionProperties {
    return SUBSCRIPTION_STATUS[option.toString()] || new EnumOptionProperties();
}

export const SUBSCRIPTION_FEE_TYPE_LABEL: EnumOptionInterface = {
    [SubscriptionFeeType.Other]: { label: 'enum.subscriptionFeeType.other', cssClass: '' },
    [SubscriptionFeeType.TaxPodFee]: { label: 'enum.subscriptionFeeType.taxPodFee', cssClass: '' },
    [SubscriptionFeeType.AAESubscriptionFee]: { label: 'enum.subscriptionFeeType.aaeSubscriptionFee', cssClass: '' },
    [SubscriptionFeeType.AskPodFee]: { label: 'enum.subscriptionFeeType.askPodFee', cssClass: '' },
};

export function getSubscriptionFeeTypeOption(option: SubscriptionFeeType): EnumOptionProperties {
    return SUBSCRIPTION_FEE_TYPE_LABEL[option] || new EnumOptionProperties();
}

export const DEFAULT_SPEAKER_IMAGE = '../../../../assets/images/speaker_placeholder.png';

export const PROGRAM_TYPE_STATUS: EnumOptionInterface = {
    [ProgramType.Course]: { label: 'text.course', cssClass: '' },
    [ProgramType.Webinar]: { label: 'text.webinar', cssClass: '' },
    [ProgramType.Podcast]: { label: 'text.podcast', cssClass: '' },
};

export function getProgramTypeOption(option: ProgramType): EnumOptionProperties {
    return PROGRAM_TYPE_STATUS[option.toString()] || new EnumOptionProperties();
}

export const WEBINAR_TYPE_STATUS: EnumOptionInterface = {
    [WebinarType.Live]: { label: 'text.live', cssClass: '' },
    [WebinarType.Recorded]: { label: 'text.past', cssClass: '' },
};

export function getWebinarTypeOption(option: WebinarType): EnumOptionProperties {
    return WEBINAR_TYPE_STATUS[option] || new EnumOptionProperties();
}

export const AAE_QUESTION_STATUS: EnumOptionInterface = {
    [AaeClientQuestionStatus.None]: { label: 'askAnExpert.allQuestions', cssClass: 'bg-[#D1F6D8] text-[#30533A]' },
    [AaeClientQuestionStatus.Draft]: { label: 'askAnExpert.draft', cssClass: 'bg-[#EBF3F9] text-[#80888E]' },
    [AaeClientQuestionStatus.NewSubmission]: {
        label: 'askAnExpert.newSubmissions',
        cssClass: 'bg-[#FFF3B8] text-[#7A734E]',
    },
    [AaeClientQuestionStatus.MoreInfoNeeded]: {
        label: 'askAnExpert.moreInfoNeeded',
        cssClass: 'bg-[#FBE7DD] text-[#FD5900]',
    },
    [AaeClientQuestionStatus.OnHold]: {
        label: 'askAnExpert.onHold',
        cssClass: 'bg-[#FFDFDF] text-[#E73A3A]',
    },
    [AaeClientQuestionStatus.InProgress]: {
        label: 'askAnExpert.inProgress',
        cssClass: 'bg-[#D7F8FC] text-[#479194]',
    },
    [AaeClientQuestionStatus.FollowUp]: {
        label: 'askAnExpert.followUp',
        cssClass: 'bg-[#F0EEFF] text-[#6A62C7]',
    },
    [AaeClientQuestionStatus.AnswerReady]: {
        label: 'askAnExpert.answerReady',
        cssClass: 'bg-[#D1F6D8] text-[#30533A]',
    },
    [AaeClientQuestionStatus.Closed]: {
        label: 'askAnExpert.closed',
        cssClass: 'bg-[#FFE9D5] text-[#9A7452]',
    },
};

export function getAaeQuestionStatusOption(option: AaeClientQuestionStatus): EnumOptionProperties {
    return AAE_QUESTION_STATUS[option.toString()] || new EnumOptionProperties();
}

export const NOTIFICATION_MODULE_TYPE_ROUTING: EnumOptionInterface = {
    [ModuleType.Course]: { label: '/program/course/details/' },
    [ModuleType.Webinar]: { label: '/program/live/details/' },
    [ModuleType.Podcast]: { label: '/program/podcast/details/' },
    [ModuleType.TaxAMinute]: { label: '/resource/tax-a-minute' },
    [ModuleType.QuickGuide]: { label: '/resource/quick-guide' },
    [ModuleType.Article]: { label: '/resource/article' },
    [ModuleType.Announcement]: { label: '/notification-listing' },
    [ModuleType.Others]: { label: '/notification-listing' },
    [ModuleType.Aae]: { label: '/ask-an-expert/aae-details/' },
};

export function getNotificationModuleTypeRoutingOption(option: ModuleType): EnumOptionProperties {
    return NOTIFICATION_MODULE_TYPE_ROUTING[option.toString()] || new EnumOptionProperties();
}

export function getCardImage(cardType: string | null): string {
    const cardImages: { [key: string]: string } = {
        maestro: 'bg-card-maestro',
        visa: 'bg-card-visa',
        mastercard: 'bg-card-mastercard',
        amex: 'bg-card-american-express',
        dinersclub: 'bg-card-diners-club',
        discover: 'bg-card-discover',
        unionpay: 'bg-card-unionpay',
        jcb: 'bg-card-jcb',
        generic: 'bg-card-generic',
    };

    return cardImages[cardType || 'bg-card-generic'] || 'bg-card-generic';
}

export function initStatusUi(status: AaeQuestionStatus, isOnHold: boolean): AaeClientQuestionStatus {
    if (
        isOnHold &&
        status !== AaeQuestionStatus.AnswerProvided &&
        status !== AaeQuestionStatus.Read &&
        status !== AaeQuestionStatus.ClosedInsufficientInfo
    ) {
        return AaeClientQuestionStatus.OnHold;
    }

    switch (status) {
        case AaeQuestionStatus.Draft:
            return AaeClientQuestionStatus.Draft;
        case AaeQuestionStatus.NewSubmission:
            return AaeClientQuestionStatus.NewSubmission;
        case AaeQuestionStatus.AwaitingCustomerInfo:
            return AaeClientQuestionStatus.MoreInfoNeeded;
        case AaeQuestionStatus.InProgress:
            return AaeClientQuestionStatus.InProgress;
        case AaeQuestionStatus.PendingReview:
            return AaeClientQuestionStatus.InProgress;
        case AaeQuestionStatus.AnswerProvided:
            return AaeClientQuestionStatus.AnswerReady;
        case AaeQuestionStatus.Read:
            return AaeClientQuestionStatus.Closed;
        case AaeQuestionStatus.ClosedInsufficientInfo:
            return AaeClientQuestionStatus.Closed;
        case AaeQuestionStatus.OnHold:
            return AaeClientQuestionStatus.OnHold;
        case AaeQuestionStatus.FollowUp:
            return AaeClientQuestionStatus.FollowUp;
        case AaeQuestionStatus.Rework:
            return AaeClientQuestionStatus.InProgress;
        default:
            return AaeClientQuestionStatus.None;
    }
}

export const QUESTION_RESPONSE_TYPE: EnumOptionInterface = {
    [AaeResponseType.Admin]: {
        label: '',
        cssClass: 'bg-[#e5f8ff]',
    },
    [AaeResponseType.InsufficientInfo]: {
        label: 'askAnExpert.infoInsufficientTitle',
        cssClass: 'bg-[#ffddd1] text-[#ed3e00]',
    },
    [AaeResponseType.OnHold]: {
        label: 'askAnExpert.onHold',
        cssClass: 'bg-[#ffdfdf] text-[#e73a3a]',
    },
};

export function getAaeResponseTypeFeatures(option: AaeResponseType): EnumOptionProperties {
    return QUESTION_RESPONSE_TYPE[option] || new EnumOptionProperties();
}

export const COUNTRY_TYPE: EnumOptionInterface = {
    [Country.Malaysia]: {
        label: 'enum.country.malaysia',
    },
    [Country.Singapore]: {
        label: 'enum.country.singapore',
    },
};

export function getCountryTypes(option: Country): EnumOptionProperties {
    return COUNTRY_TYPE[option] || new EnumOptionProperties();
}
